import { PageChrome } from "@/components/page-chrome"
import { SaveButton } from "@/components/shared/action-buttons"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { FormGroup } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { PageBody } from "@/components/ui/layout"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { FormRenderer } from "@/lib/data-driven-forms/renderer"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import { UsersRegistrationsAccount } from "@/types/serializers"
import { Lock, User } from "lucide-react"
import { Activity, useId, useState } from "react"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { useNavigate, useProps } from "@/lib/page-rendering"

const accountSchema: DataDrivenFormSchema<"users"> = {
  type: "form",
  validator: z.object({
    first_name: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
    last_name: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
    current_password: z.string().min(6, VALIDATION_ERROR_KEYS.REQUIRED),
  }),
  namespace: "users",
  properties: [
    {
      type: "field:input",
      name: "first_name",
      label: "form.account.first_name",
      inputType: "text",
    },
    {
      type: "field:input",
      name: "last_name",
      label: "form.account.last_name",
      inputType: "text",
    },
    {
      type: "field:input",
      name: "current_password",
      label: "form.password.current_password",
      inputType: "password",
    },
  ],
}

const passwordSchema: DataDrivenFormSchema<"users"> = {
  type: "form",
  validator: z
    .object({
      current_password: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
      password: z
        .string()
        .trim()
        .min(1, VALIDATION_ERROR_KEYS.REQUIRED)
        .min(6, "users:form.errors.password_minimum"),
      password_confirmation: z
        .string()
        .trim()
        .min(1, VALIDATION_ERROR_KEYS.REQUIRED)
        .min(6, "users:form.errors.password_minimum"),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: "users:form.errors.password_mismatch",
      path: ["password_confirmation"],
    }),
  namespace: "users",
  properties: [
    {
      type: "field:input",
      name: "current_password",
      label: "form.password.current_password",
      inputType: "password",
    },
    {
      type: "field:input",
      name: "password",
      label: "form.password.new_password",
      inputType: "password",
    },
    {
      type: "field:input",
      name: "password_confirmation",
      label: "form.password.new_password_confirmation",
      inputType: "password",
    },
  ],
}

export default function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation("users", { keyPrefix: "form" })
  const accountFormId = useId()
  const passwordFormId = useId()
  const { email, first_name, last_name } = useProps<{
    account: UsersRegistrationsAccount
  }>().account
  const [activeTab, setActiveTab] = useState<"password" | "account">("account")
  const formId = activeTab === "account" ? accountFormId : passwordFormId
  // Account is a root, like the workspace and the search index: the
  // header's user chip opens it, so it is its own only crumb.
  return (
    <>
      <PageChrome
        icon={User}
        title={t("account.title")}
        action={<SaveButton form={formId} />}
      />
      <PageBody>
        <Card>
          <CardHeader>
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as "account" | "password")
              }
            >
              <TabsList>
                <TabsTrigger value="account">
                  <User />
                  {t("account.tab")}
                </TabsTrigger>
                <TabsTrigger value="password">
                  <Lock />
                  {t("password.tab")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <Activity mode={activeTab === "account" ? "visible" : "hidden"}>
              <FormGroup
                htmlFor="email-id"
                label={t("account.email")}
                className="mb-5"
              >
                <Input id="email-id" name="email" disabled value={email} />
              </FormGroup>
              <FormRenderer
                schema={accountSchema}
                formId={accountFormId}
                formLabel={t("account.tab")}
                onSubmit={(data) => {
                  navigate({
                    href: "/users",
                    method: "patch",
                    data: { user: data },
                  })
                }}
                defaultValues={{ last_name, first_name }}
              />
            </Activity>

            <Activity mode={activeTab === "password" ? "visible" : "hidden"}>
              <FormRenderer
                schema={passwordSchema}
                formId={passwordFormId}
                formLabel={t("password.tab")}
                onSubmit={(data) =>
                  navigate({
                    href: "/users",
                    method: "patch",
                    data: { user: data },
                  })
                }
                defaultValues={{}}
              />
            </Activity>
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
